import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { MentionMenu }
  from 'Pages/TeamDashboard/CreateReview/components/AskAnything/ChatComposer/components/MentionMenu';
import askAnythingStyles from 'Pages/TeamDashboard/CreateReview/components/AskAnything/styles.module.scss';

import {
  filterMentionOptions,
  getMentionLabel,
  parseMentionText,
  serializeMention,
} from 'utlis/chatPrototype';

import styles from './styles.module.scss';

const DEFAULT_LINE_HEIGHT = 20;
const MENU_WIDTH = 285;
const MENU_GAP = 8;
const MODIFIER_KEYS = ['Shift', 'Control', 'Alt', 'Meta', 'CapsLock'];

// to place a finding button in the text, we need to serialize the text to a string that can be used to render the button
function serialize(editor) {
  return Array.from(editor.childNodes).map(node => {
    if (node.dataset?.feedbackId) {
      return serializeMention({ feedbackId: node.dataset.feedbackId, title: node.dataset.title });
    }

    return node.nodeName === 'BR' ? '\n' : node.textContent;
  })
    .join('');
}

function createChip(option) {
  const chip = document.createElement('span');

  chip.className = cn(askAnythingStyles['feedback-reference-button'], styles['chat-composer__chip']);
  chip.setAttribute('contenteditable', 'false');
  chip.dataset.feedbackId = String(option.feedbackId);
  chip.dataset.title = option.title;
  chip.textContent = getMentionLabel(option);

  return chip;
}

function appendText(container, text) {
  text.split('\n').forEach((line, index) => {
    if (index > 0) {
      container.appendChild(document.createElement('br'));
    }

    if (line) {
      container.appendChild(document.createTextNode(line));
    }
  });
}

function setCaret(node, offset) {
  const range = document.createRange();
  const selection = window.getSelection();

  range.setStart(node, offset);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
}

export function ChatComposer({
  value,
  onChange,
  onKeyDown,
  onLineCountChange,
  placeholder,
  disabled,
  isSingleLine,
  isPreview,
  mentionOptions,
}) {
  const [menuPosition, setMenuPosition] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [query, setQuery] = useState('');

  const rootRef = useRef(null);
  const editorRef = useRef(null);
  const pillRef = useRef(null);
  const triggerRef = useRef(null); // the text node that starts with the typed "#"
  const lastEmittedRef = useRef(null);

  const filteredOptions = useMemo(() => filterMentionOptions(mentionOptions, query), [mentionOptions, query]);

  const syncLineCount = useCallback(() => {
    const editor = editorRef.current;

    if (!editor || !onLineCountChange) {
      return;
    }

    const computed = window.getComputedStyle(editor);
    const lineHeight = parseFloat(computed.lineHeight) || DEFAULT_LINE_HEIGHT;
    const padding = (parseFloat(computed.paddingTop) || 0) + (parseFloat(computed.paddingBottom) || 0);

    onLineCountChange(Math.max(1, Math.round((editor.scrollHeight - padding) / lineHeight)));
  }, [onLineCountChange]);

  const updateEmptyState = useCallback(text => {
    editorRef.current.dataset.empty = String(/^\n*$/.test(text));
  }, []);

  // Re-render the field when the value is changed from outside (clear after send, prompt preview)
  useLayoutEffect(() => {
    const editor = editorRef.current;

    if (value === lastEmittedRef.current) {
      return;
    }

    editor.textContent = '';
    parseMentionText(value, mentionOptions).forEach(segment => {
      if (segment.type === 'mention') {
        editor.appendChild(createChip(segment.option));
      }
      else {
        appendText(editor, segment.value);
      }
    });

    lastEmittedRef.current = value;
    updateEmptyState(value);

    if (document.activeElement === editor) {
      setCaret(editor, editor.childNodes.length);
    }
  }, [value, mentionOptions, updateEmptyState]);

  useLayoutEffect(() => {
    syncLineCount();
  }, [value, isSingleLine, syncLineCount]);

  // Re-wrap when the composer width changes
  useEffect(() => {
    const editor = editorRef.current;
    let lastWidth = editor.clientWidth;
    // eslint-disable-next-line compat/compat -- ResizeObserver reflows the composer when its width changes
    const observer = new ResizeObserver(() => {
      if (editor.clientWidth !== lastWidth) {
        lastWidth = editor.clientWidth;
        syncLineCount();
      }
    });

    observer.observe(editor);

    return () => observer.disconnect();
  }, [syncLineCount]);

  const emitChange = useCallback(() => {
    const text = serialize(editorRef.current);

    lastEmittedRef.current = text;
    updateEmptyState(text);
    onChange(text);
    syncLineCount();
  }, [onChange, syncLineCount, updateEmptyState]);

  // Typing "#" wraps it in a "#| Select a finding" box and opens the menu next to it
  const openMenu = useCallback(() => {
    const editor = editorRef.current;
    const { anchorNode, anchorOffset } = window.getSelection();
    const isStartOfWord = anchorNode?.nodeType === Node.TEXT_NODE
      && anchorNode.parentNode === editor
      && (anchorOffset === 1 || /\s/.test(anchorNode.nodeValue.charAt(anchorOffset - 2)));
    const hasFinding = Boolean(editor.querySelector('[data-feedback-id]'));

    if (mentionOptions.length === 0 || hasFinding || !isStartOfWord) {
      return;
    }

    const hash = anchorNode.splitText(anchorOffset - 1);
    const pill = document.createElement('span');

    if (hash.length > 1) {
      hash.splitText(1);
    }

    pill.className = styles['chat-composer__pill'];
    hash.before(pill);
    pill.appendChild(hash);
    setCaret(hash, 1);
    pillRef.current = pill;
    triggerRef.current = hash;

    const pillRect = pill.getBoundingClientRect();
    const rootRect = rootRef.current.getBoundingClientRect();

    setQuery('');
    setHighlightedIndex(-1);
    setMenuPosition({
      left: Math.min(pillRect.left - rootRect.left, Math.max(0, rootRect.width - MENU_WIDTH)),
      bottom: rootRect.bottom - pillRect.top + MENU_GAP,
    });
  }, [mentionOptions.length]);

  // Turns the "#| Select a finding" box back into plain "#" text, keeping the caret where it was
  const unwrapPill = useCallback(() => {
    const pill = pillRef.current;

    if (!pill) {
      return;
    }

    pillRef.current = null;

    if (!pill.parentNode) {
      return;
    }

    const { anchorNode, anchorOffset } = window.getSelection();
    const hasCaret = document.activeElement === editorRef.current && pill.contains(anchorNode);

    while (pill.firstChild) {
      pill.parentNode.insertBefore(pill.firstChild, pill);
    }

    pill.remove();

    if (hasCaret && anchorNode.isConnected) {
      const nodeLength = anchorNode.nodeType === Node.TEXT_NODE ? anchorNode.length : anchorNode.childNodes.length;

      setCaret(anchorNode, Math.min(anchorOffset, nodeLength));
    }
  }, []);

  const closeMenu = useCallback(() => {
    unwrapPill();
    triggerRef.current = null;
    setMenuPosition(null);
  }, [unwrapPill]);

  // Replaces the typed "#..." text with the finding button
  const selectOption = useCallback(option => {
    const hash = triggerRef.current;

    if (!hash) {
      return;
    }

    const chip = createChip(option);
    const spacer = document.createTextNode(' ');
    const range = document.createRange();

    unwrapPill();
    range.setStart(hash, 0);
    range.setEnd(hash, hash.length);
    range.deleteContents();
    range.insertNode(chip);
    chip.after(spacer);
    setCaret(spacer, 1);
    triggerRef.current = null;
    setMenuPosition(null);
    emitChange();
  }, [emitChange, unwrapPill]);

  const handleInput = useCallback(event => {
    emitChange();

    if (!menuPosition) {
      if (event.nativeEvent.data === '#') {
        openMenu();
      }

      return;
    }

    // The text typed after "#" filters the menu; a space (or deleting the "#") closes it
    const text = triggerRef.current?.isConnected ? triggerRef.current.nodeValue : '';

    if (text.charAt(0) !== '#' || /\s/.test(text)) {
      closeMenu();

      return;
    }

    setQuery(text.slice(1));
    setHighlightedIndex(-1);
  }, [closeMenu, emitChange, menuPosition, openMenu]);

  const handleKeyDown = useCallback(event => {
    if (event.nativeEvent.isComposing) {
      return;
    }

    if (menuPosition) {
      if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && filteredOptions.length > 0) {
        event.preventDefault();
        setHighlightedIndex(
          index => (event.key === 'ArrowDown'
            ? Math.min(index + 1, filteredOptions.length - 1)
            : Math.max(index - 1, 0))
        );

        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu();

        return;
      }

      if (event.key === 'Enter' && filteredOptions.length > 0) {
        event.preventDefault();

        if (highlightedIndex >= 0) {
          selectOption(filteredOptions[highlightedIndex]);
        }

        return;
      }

      // Typed characters go after the "#" (the box around it goes away); moving the caret closes the menu
      if (event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.metaKey) {
        unwrapPill();
      }
      else if (event.key !== 'Backspace' && !MODIFIER_KEYS.includes(event.key)) {
        closeMenu();
      }
    }

    // Only one new idea per message: "#" does nothing while a finding button is in the field
    if (event.key === '#' && editorRef.current.querySelector('[data-feedback-id]')) {
      event.preventDefault();

      return;
    }

    onKeyDown?.(event);
  }, [closeMenu, filteredOptions, highlightedIndex, menuPosition, onKeyDown, selectOption, unwrapPill]);

  // Paste as plain text only
  const handlePaste = useCallback(event => {
    event.preventDefault();

    const text = event.clipboardData.getData('text/plain').replace(/\r\n?/g, '\n');
    const selection = window.getSelection();

    if (!text || selection.rangeCount === 0) {
      return;
    }

    const range = selection.getRangeAt(0);
    const fragment = document.createDocumentFragment();

    appendText(fragment, text);

    const lastNode = fragment.lastChild;

    range.deleteContents();
    range.insertNode(fragment);
    range.setStartAfter(lastNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    emitChange();
  }, [emitChange]);

  const handleDrop = useCallback(event => event.preventDefault(), []);

  const handleHover = useCallback(index => setHighlightedIndex(index), []);

  return (
    <div ref={rootRef} className={styles['chat-composer-wrapper']}>
      <div
        ref={editorRef}
        role="textbox"
        aria-multiline="true"
        aria-label={placeholder}
        contentEditable={!disabled}
        aria-disabled={disabled}
        suppressContentEditableWarning
        spellCheck
        data-placeholder={placeholder}
        className={cn(styles['chat-composer'], {
          [styles['chat-composer--single-line']]: isSingleLine,
          [styles['chat-composer--preview']]: isPreview,
        })}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onDrop={handleDrop}
        onMouseDown={closeMenu}
        onBlur={closeMenu}
      />
      {menuPosition && (
        <MentionMenu
          options={filteredOptions}
          highlightedIndex={highlightedIndex}
          position={menuPosition}
          onHover={handleHover}
          onSelect={selectOption}
        />
      )}
    </div>
  );
}

ChatComposer.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func,
  onLineCountChange: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  isSingleLine: PropTypes.bool,
  isPreview: PropTypes.bool,
  mentionOptions: PropTypes.arrayOf(PropTypes.shape({
    insightUuid: PropTypes.string.isRequired,
    feedbackId: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
  })),
};

ChatComposer.defaultProps = {
  onKeyDown: undefined,
  onLineCountChange: undefined,
  placeholder: '',
  disabled: false,
  isSingleLine: false,
  isPreview: false,
  mentionOptions: [],
};
